import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import "./PublicHeader.css";

interface PublicHeaderProps {
  backTo?: string;
  backLabel?: string;
  onLogin?: () => void;
  overlay?: boolean;
}

export function workspaceFor(role: string | null) {
  return role === "coach" ? "/dashboard" : "/admin";
}

export function workspaceLabelKey(role: string | null) {
  if (role === "coach") return "nav.backToCoachPortal";
  if (role === "ranking_admin") return "nav.backToRankingAdministration";
  return "nav.backToAdministration";
}

export function workspaceAriaLabelKey(role: string | null) {
  if (role === "coach") return "nav.returnToCoachPortal";
  if (role === "ranking_admin") return "nav.returnToRankingAdministration";
  return "nav.returnToAdministration";
}

function roleKey(role: string | null) {
  if (role === "ranking_admin") return "nav.role.rankingAdmin";
  if (role === "admin") return "nav.role.admin";
  return "nav.role.coach";
}

export default function PublicHeader({ backTo, backLabel, onLogin, overlay = false }: PublicHeaderProps) {
  const { currentUser, userRole, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const login = () => onLogin ? onLogin() : navigate("/login");

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <header className={`public-site-header${overlay ? " public-site-header--overlay" : ""}`}>
      <div className="public-site-header__identity">
        {backTo && (
          <Link className="public-site-header__back" to={backTo} aria-label={t("common.back")}>
            <span aria-hidden="true">←</span>
            <span className="public-site-header__back-label">{backLabel || t("common.back")}</span>
          </Link>
        )}
        <Link className="public-site-header__brand" to="/">
          <img src="/geo.png" alt="" />
          <span>
            <strong>GEORGIAN TAEKWONDO</strong>
            <small>NATIONAL FEDERATION</small>
          </span>
        </Link>
      </div>

      <nav className="public-site-header__links" aria-label={t("nav.publicNavigation")}>
        <Link className={location.pathname === "/" ? "is-active" : ""} to="/">
          {t("nav.home")}
        </Link>
        <Link className={location.pathname.startsWith("/events") || location.pathname.startsWith("/competition/") ? "is-active" : ""} to="/events">
          {t("nav.publicEvents")}
        </Link>
        <Link className={location.pathname.startsWith("/athletes") ? "is-active" : ""} to="/athletes">
          {t("nav.athletes")}
        </Link>
        <Link className={location.pathname.startsWith("/clubs") ? "is-active" : ""} to="/clubs">
          {t("nav.clubs")}
        </Link>
        <Link className={location.pathname.startsWith("/rankings") ? "is-active" : ""} to="/rankings">
          {t("nav.publicRankings")}
        </Link>
      </nav>

      <div className="public-site-header__actions">
        <ThemeSwitcher compact />
        <LanguageSwitcher />
        {!loading && currentUser ? (
          <div className="public-site-header__desktop-session">
            <div className="public-site-header__account" title={currentUser.email || undefined}>
              <span>{t(roleKey(userRole))}</span>
              <small>{currentUser.email}</small>
            </div>
            <Link
              className="public-site-header__primary"
              to={workspaceFor(userRole)}
              aria-label={t(workspaceAriaLabelKey(userRole))}
              title={t(workspaceAriaLabelKey(userRole))}
            >
              {t(workspaceLabelKey(userRole))}
            </Link>
          </div>
        ) : !loading ? (
          <button className="public-site-header__primary" type="button" onClick={login}>
            {t("login.button")}
          </button>
        ) : (
          <span className="public-site-header__auth-loading" aria-hidden="true" />
        )}
        <button
          className="public-site-header__menu-button"
          type="button"
          aria-label={t("nav.menu")}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span /><span /><span />
        </button>
      </div>
      {menuOpen && (
        <nav className="public-site-header__mobile-menu" aria-label={t("nav.publicNavigation")}>
          <Link to="/">{t("nav.home")}</Link>
          <Link to="/events">{t("nav.publicEvents")}</Link>
          <Link to="/athletes">{t("nav.athletes")}</Link>
          <Link to="/clubs">{t("nav.clubs")}</Link>
          <Link to="/rankings">{t("nav.publicRankings")}</Link>
          {!loading && currentUser && (
            <div className="public-site-header__mobile-session">
              <span>{t(roleKey(userRole))}</span>
              <small>{currentUser.email}</small>
              <Link
                className="public-site-header__mobile-workspace"
                to={workspaceFor(userRole)}
                aria-label={t(workspaceAriaLabelKey(userRole))}
                title={t(workspaceAriaLabelKey(userRole))}
              >
                {t(workspaceLabelKey(userRole))}
              </Link>
            </div>
          )}
        </nav>
      )}
    </header>
  );
}
